import React, { useState } from 'react';
import './QuizDetailView.css';

const getScoreColor = (score) => {
    const s = Number(score);
    if (s >= 4) return '#2E5945'; // Success Green
    if (s === 3) return '#A0522D'; // Orange
    return '#8B3A3A'; // Red
};

const QuizDetailView = ({ quiz, onClose }) => {
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const [activeTooltip, setActiveTooltip] = useState(null);

    // --- HELPER: Resolve Model Answer ---
    const resolveModel = (attempt, quiz) => {
        // 1. Try to get the model answer the AI used during this specific attempt
        const aiFeedback = attempt?.feedback;
        if (aiFeedback?.model_answer) return aiFeedback.model_answer;

        // 2. Fallback to the quiz's original answer key
        if (quiz?.answer_key) {
            try {
                return typeof quiz.answer_key === 'string' 
                    ? JSON.parse(quiz.answer_key) 
                    : quiz.answer_key;
            } catch (e) { return null; }
        }
        return null;
    };

    const handleAnnotationClick = (e, aiFeedback) => {
        const marker = e.target.closest('span[id]');
        if (!marker) {
            setActiveTooltip(null);
            return;
        }
        e.stopPropagation();

        const id = parseInt(marker.getAttribute('id'), 10);
        const commentObj = aiFeedback?.comments?.find(c => c.id === id);

        if (commentObj) {
            const rect = marker.getBoundingClientRect();
            setActiveTooltip({
                text: commentObj.feedback,
                x: rect.left + rect.width / 2,
                y: rect.top - 10
            });
        }
    };

    const DetailView = ({ attempt }) => {
        const aiFeedback = attempt?.feedback;
        const model = resolveModel(attempt, quiz);
        const score = attempt.userScore || attempt.score || aiFeedback?.score_suggestion || 0;

        // Use AI annotated HTML if it exists, otherwise use raw text
        const displayHtml = (aiFeedback?.annotated_user_answer_html || attempt.userAttempt || "")
            .replace(/<span id="/g, '<span class="ai-annotation" id="');

        return (
            <div className="history-detail-view" onClick={() => setActiveTooltip(null)}>
                <div className="detail-header">
                    <button className="back-link" onClick={() => setSelectedAttempt(null)}>
                        <span className="material-symbols-outlined">arrow_back</span> Back to List
                    </button>
                    <div className="score-pill" style={{ backgroundColor: getScoreColor(score) }}>
                        Score: {score}/5
                    </div>
                </div>

                {activeTooltip && (
                    <div className="modal-tooltip-box visible" style={{ left: activeTooltip.x, top: activeTooltip.y }}>
                        {activeTooltip.text}
                        <div className="tooltip-arrow" />
                    </div>
                )}

                <div className="detail-split-container">
                    <div className="detail-panel model-side">
                        <h4 className="panel-label">Model Analysis</h4>
                        <div className="scroll-area">
                            {model ? (
                                <>
                                    <div className="model-block"><strong>Issue:</strong><p>{model.issue}</p></div>
                                    <div className="model-block"><strong>Rule:</strong><p>{model.rule}</p></div>
                                    <div className="model-block"><strong>Analysis:</strong><p>{model.analysis}</p></div>
                                    <div className="model-block"><strong>Conclusion:</strong><p>{model.conclusion}</p></div>
                                </>
                            ) : <p className="text-muted">No model answer available.</p>}
                        </div>
                    </div>

                    <div className="detail-panel user-side" onClick={(e) => handleAnnotationClick(e, aiFeedback)}>
                        <h4 className="panel-label">Your Answer (Click highlights for feedback)</h4>
                        <div className="scroll-area annotated-content">
                            <div dangerouslySetInnerHTML={{ __html: displayHtml }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const ListView = () => {
        const attempts = quiz.attempt_history || [];
        return (
            <div className="history-list-container">
                <header className="document-header" style={{ padding: '25px', textAlign: 'left' }}>
                    <h1 className="document-title" style={{ fontSize: '1.2rem' }}>{quiz.ruleName}</h1>
                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>{attempts.length} Total Attempts</p>
                </header>
                <table className="history-table">
                    <thead>
                        <tr><th>Date</th><th>Score</th><th>Type</th><th></th></tr>
                    </thead>
                    <tbody>
                        {[...attempts].reverse().map((att, i) => {
                            const isAi = !!att.feedback?.annotated_user_answer_html;
                            const displayScore = att.userScore || att.score || att.feedback?.score_suggestion || '-';
                            return (
                                <tr key={i} className="clickable-row" onClick={() => setSelectedAttempt(att)}>
                                    <td>{new Date(att.timestamp).toLocaleDateString()}</td>
                                    <td><span className="score-badge" style={{ background: getScoreColor(displayScore) }}>{displayScore}/5</span></td>
                                    <td>{isAi ? "AI Graded" : "Manual"}</td>
                                    <td><span className="material-symbols-outlined">chevron_right</span></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="history-modal theme-aware" style={{ maxWidth: '950px', height: '85vh' }} onClick={e => e.stopPropagation()}>
                <div className="modal-toolbar-pane">
                    <div className="toolbar-context">
                        <span className="material-symbols-outlined">history</span>
                        <span>Quiz Analytics</span>
                    </div>
                    <button className="icon-btn close-btn" onClick={onClose}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className="modal-content-pane">
                    {selectedAttempt ? <DetailView attempt={selectedAttempt} /> : <ListView />}
                </div>
            </div>
        </div>
    );
};

export default QuizDetailView;